import type {FC} from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {styled} from 'styled-components';

import type {NzbCheckStats} from '@repo/shared/api/website_api';
import {
  addNzbCheckStats,
  createEmptyNzbCheckStats,
  NZB_RECHECK_AFTER_DAYS,
} from '@repo/shared/lib/nzb_check_stats';

import {Button, ButtonAsLink, UnthemedNavLink} from '@repo/shared-web/components/core/button';
import {LoadingIndicator} from '@repo/shared-web/components/core/loading_indicator';

import {apiCall} from '@repo/website-frontend/lib/api';

interface AdminPageProps {}

interface StatsProgress {
  stats: NzbCheckStats;
  asOf: number | undefined;
  paginationToken: string | undefined;
}

interface StatsSnapshot {
  stats: NzbCheckStats;
  loadedAt: number;
}

type LoadState = 'idle' | 'loading' | 'paused' | 'error' | 'loaded';
const numberFormat = new Intl.NumberFormat();
const rows = [
  {key: 'upToDate', label: 'Up to date'},
  {key: 'neverChecked', label: 'Never checked'},
  {key: 'needsRecheck', label: 'Needs rechecking'},
] as const;

export const AdminPage: FC<AdminPageProps> = () => {
  const [snapshot, setSnapshot] = useState<StatsSnapshot>();
  const [state, setState] = useState<LoadState>('idle');
  const [counted, setCounted] = useState(0);
  const [error, setError] = useState<string>();
  const progressRef = useRef<StatsProgress>();
  const runRef = useRef(0);
  const loading = state === 'loading';

  useEffect(
    () => () => {
      runRef.current++;
    },
    [],
  );

  const handleLoad = useCallback(async () => {
    const run = ++runRef.current;
    let progress = progressRef.current ?? {
      stats: createEmptyNzbCheckStats(),
      asOf: undefined,
      paginationToken: undefined,
    };
    setCounted(progress.stats.all.total);
    setError(undefined);
    setState('loading');
    try {
      while (true) {
        const page = await apiCall('POST /get-admin-nzb-check-stats', {
          asOf: progress.asOf,
          paginationToken: progress.paginationToken,
        });
        if (runRef.current !== run) return;
        progress = {
          stats: addNzbCheckStats(progress.stats, page.stats),
          asOf: page.asOf,
          paginationToken: page.paginationToken,
        };
        progressRef.current = progress;
        setCounted(progress.stats.all.total);
        if (page.paginationToken === undefined) {
          setSnapshot({stats: progress.stats, loadedAt: Date.now()});
          progressRef.current = undefined;
          setState('loaded');
          return;
        }
      }
    } catch (err: unknown) {
      if (runRef.current !== run) return;
      setError(err instanceof Error ? err.message : 'Could not load stats');
      setState('error');
    }
  }, []);

  const handleStop = useCallback(() => {
    runRef.current++;
    setState('paused');
  }, []);

  const handleRestart = useCallback(async () => {
    progressRef.current = undefined;
    await handleLoad();
  }, [handleLoad]);

  return (
    <Wrapper>
      <h1>Admin</h1>
      <Section aria-labelledby="admin-nzb-checks">
        <SectionHeader>
          <h2 id="admin-nzb-checks">NZB checks</h2>
          <Actions>
            <ChecksLink to="/debug/nzb-health">View checks</ChecksLink>
            <Button onClickAsync={handleLoad} disabled={loading}>
              {loading
                ? 'Loading stats…'
                : state === 'paused' || state === 'error'
                  ? 'Resume stats'
                  : snapshot
                    ? 'Refresh stats'
                    : 'Load stats'}
            </Button>
            {loading ? <ButtonAsLink onClick={handleStop}>Stop</ButtonAsLink> : null}
            {(state === 'paused' || state === 'error') && counted > 0 ? (
              <ButtonAsLink onClickAsync={handleRestart}>Start over</ButtonAsLink>
            ) : null}
          </Actions>
        </SectionHeader>

        {loading || state === 'paused' || state === 'error' ? (
          <Progress role="status" aria-live="polite">
            {loading ? <LoadingIndicator size={18} /> : null}
            <span>
              {state === 'paused' ? 'Paused · ' : state === 'error' ? 'Stopped · ' : ''}
              {numberFormat.format(counted)} NZBs counted{loading ? '…' : ''}
            </span>
          </Progress>
        ) : null}
        {error ? <ErrorMessage role="alert">{error}</ErrorMessage> : null}

        {snapshot ? (
          <>
            <TableScroll aria-busy={loading}>
              <StatsTable>
                <caption>NZBs in the catalog, grouped by their latest check</caption>
                <thead>
                  <tr>
                    <th scope="col">Status</th>
                    <th scope="col">NZBs</th>
                    <th scope="col">Healthy</th>
                    <th scope="col">Unhealthy</th>
                    <th scope="col" title="Never checked, failed, or no conclusive sample">
                      Unknown
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({key, label}) => (
                    <tr key={key}>
                      <th scope="row">{label}</th>
                      <td>{numberFormat.format(snapshot.stats[key].total)}</td>
                      <Healthy>{numberFormat.format(snapshot.stats[key].healthy)}</Healthy>
                      <Unhealthy>{numberFormat.format(snapshot.stats[key].unhealthy)}</Unhealthy>
                      <Unknown>{numberFormat.format(snapshot.stats[key].unknown)}</Unknown>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">All NZBs</th>
                    <td>{numberFormat.format(snapshot.stats.all.total)}</td>
                    <Healthy>{numberFormat.format(snapshot.stats.all.healthy)}</Healthy>
                    <Unhealthy>{numberFormat.format(snapshot.stats.all.unhealthy)}</Unhealthy>
                    <Unknown>{numberFormat.format(snapshot.stats.all.unknown)}</Unknown>
                  </tr>
                </tfoot>
              </StatsTable>
            </TableScroll>
            <Metadata>Updated {new Date(snapshot.loadedAt).toLocaleString()}</Metadata>
          </>
        ) : state === 'idle' ? (
          <EmptyState>Load stats to count all NZBs in the catalog.</EmptyState>
        ) : null}

        <Notes>
          Recheck after {NZB_RECHECK_AFTER_DAYS} days, or after a failed or empty check.
          <br />
          Health reflects the latest sample. Automatic checks currently cover never-checked NZBs.
        </Notes>
      </Section>
    </Wrapper>
  );
};
AdminPage.displayName = 'AdminPage';

const Wrapper = styled.div`
  width: 100%;
  max-width: 1120px;
  box-sizing: border-box;
  padding: 24px;
  margin: 0 auto;
  h1 {
    font-size: 26px;
    margin: 0 0 20px;
  }
  @media (max-width: 600px) {
    padding: 16px;
  }
`;

const Section = styled.section`
  background: #1b202a;
  border: 1px solid #ffffff12;
  border-radius: 8px;
  padding: 20px;
  min-width: 0;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  h2 {
    font-size: 18px;
    margin: 0;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const ChecksLink = styled(UnthemedNavLink)`
  color: #a9d2ff;
  font-size: 13px;
`;

const Progress = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #ffffffbb;
  font-size: 14px;
  margin: 12px 0;
`;

const EmptyState = styled.div`
  padding: 24px 0;
  color: #ffffff99;
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const StatsTable = styled.table`
  border-collapse: collapse;
  table-layout: auto;
  min-width: 100%;
  width: max-content;
  font-variant-numeric: tabular-nums;
  caption {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
  th,
  td {
    padding: 9px 14px;
    white-space: nowrap;
    text-align: right;
    border-bottom: 1px solid #ffffff12;
  }
  th:first-child {
    text-align: left;
    padding-left: 0;
  }
  th:last-child,
  td:last-child {
    padding-right: 0;
  }
  thead th {
    color: #ffffff88;
    font-size: 13px;
    font-weight: 500;
  }
  tbody th {
    font-weight: 400;
  }
  tfoot th,
  tfoot td {
    padding-top: 13px;
    font-weight: 600;
    border-bottom: 0;
  }
`;

const Healthy = styled.td`
  color: #9ee6ae;
`;

const Unhealthy = styled.td`
  color: #ff9c96;
`;

const Unknown = styled.td`
  color: #ffffff88;
`;

const Metadata = styled.div`
  margin-top: 12px;
  color: #ffffff77;
  font-size: 12px;
`;

const Notes = styled.p`
  color: #ffffff88;
  font-size: 12px;
  line-height: 1.7;
  margin: 16px 0 0;
`;

const ErrorMessage = styled.div`
  color: #ff9c96;
  font-size: 14px;
  margin: 12px 0;
`;
